import React, {Fragment} from 'react';
import styled from 'styled-components';
import {useHistory, useLocation} from 'react-router-dom';
import NikePOSIcon from '../NikePOSIcon';
import SGBody from 'components/Typography/SGBody';
import Modal from '../../../components/Modal';
import NetworkCircuitModalContent from '../../../components/ModalContent/NetworkCircuitModalContent';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import Device from 'features/POSRegisters/interfaces/Device';
import DeviceType, {DeviceTypeString} from 'features/POSRegisters/interfaces/DeviceType';
import IconButton from '@material-ui/core/IconButton';
import POSState from 'features/POSRegisters/interfaces/POSState';
import ScanDetails from 'features/POSRegisters/interfaces/ScanDetails';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';
import ReceiptPrinterModalContent from '../../../components/ModalContent/ReceiptPrinterModalContent';

//#endregion Styled Components
const StyledNodeItem = styled(IconButton)`
  background-color: transparent;
  color: ${props => props.theme.white};
  width: 80px;
  border: none;
  transition: ease-in-out 150ms;
  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }
  &.disabled:hover {
    cursor: default;
    opacity: 1;
  }
`;

const StyledNodeContainer = styled.div<{$area: string; $nodeIndex: number}>`
  background-color: ${props => props.theme.cardBg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  grid-area: ${props => props.$area + '-' + props.$nodeIndex};
  border: none;
  padding: 0px;
  &.disabled {
    opacity: 0.5;
    cursor: default;
  }
  &.disabled:hover {
    opacity: 0.5;
    cursor: default;
  }
`;

const StyledIconContainer = styled.div`
  position: relative;
  background-color: ${props => props.theme.disabledStateBlue}66;
  min-width: 80px;
  min-height: 80px;
  border-radius: 10px;
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid transparent;
  &.minor {
    border: 2px solid ${props => props.theme.yellow};
  }

  &.major {
    border: 2px solid ${props => props.theme.orange};
  }

  &.critical {
    border: 2px solid ${props => props.theme.red};
  }
`;

const StyledLabel = styled(SGBody)`
  font-family: 'Trade Gothic Bold Condensed';
  text-transform: capitalize;
`;

const StyledSeverityBadge = styled(SeverityBadge)`
  position: absolute;
  top: -12px;
  right: -12px;
`;
//#endregion Styled Components

//#region Helper Functions
function renderModalContent(state: POSState, deviceType: DeviceType, deviceName?: string) {
  switch (deviceType) {
    case DeviceType.CLOUD_SERVICES:
      return <>Cloud Services</>;
    case DeviceType.NETWORK_CIRCUIT:
      return (
        <NetworkCircuitModalContent
          devices={state.networkCircuits}
          deviceType={deviceType}
          deviceName={deviceName}
        />
      );
    case DeviceType.NETWORK_SWITCH:
      return <>Network Switch</>;
    default:
      return <>Unhandeled Device Type</>;
  }
}

function useQuery() {
  const {search} = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

//#endregion Helper Functions

//#region Component
interface NodeItemProps {
  state: POSState;
  devices: Device<ScanDetails>[];
  deviceType: DeviceType;
  deviceName?: string;
  index: number;
}

export default function NodeItem({state, devices, deviceName, deviceType, index}: NodeItemProps) {
  const history = useHistory();
  let query = useQuery();
  const siteIdParam: string | null = query.get('siteId');

  const [open, setOpen] = React.useState(false);

  const severity = getPriorityAlert(devices);
  const hasIssues = Number(SeverityPriorityEnum[severity.toUpperCase() as any]) > 1;

  const handleOpen = () => {
    if (deviceType === DeviceType.CLOUD_SERVICES) return;
    if (deviceType === DeviceType.NETWORK_SWITCH && siteIdParam !== null) {
      history.push(`/site-search/switch?siteId=${siteIdParam}`);
      return;
    }

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment key={deviceName}>
      <StyledNodeContainer
        $area={deviceType}
        $nodeIndex={index}
        className={deviceType === DeviceType.CLOUD_SERVICES ? 'disabled' : ''}
      >
        <StyledIconContainer className={severity}>
          {hasIssues && <StyledSeverityBadge severity={severity} size={1} />}
          <StyledNodeItem
            onClick={handleOpen}
            className={deviceType === DeviceType.CLOUD_SERVICES ? 'disabled' : ''}
          >
            <NikePOSIcon deviceType={deviceType} />
          </StyledNodeItem>
        </StyledIconContainer>
        <StyledLabel variant="b4">{DeviceTypeString[deviceType]}</StyledLabel>
        {deviceName && <SGBody variant="b4">{deviceName}</SGBody>}
      </StyledNodeContainer>

      <Modal open={open} onClose={handleClose}>
        {renderModalContent(state, deviceType, deviceName)}
      </Modal>
    </Fragment>
  );
}
//#endregion Component
